import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, Building2, CheckCircle2, Clock, ShieldCheck, PhoneCall, XCircle } from 'lucide-react';
import { useLang, LangToggle } from '@/lib/i18n';

/**
 * `/r/:token` — the patient's own page for a patient-direct request (ADR 0008).
 *
 * Once a practice accepts, the patient sees its name on the same "Is this your
 * doctor's office?" check the QR intake uses (decision 5's second check) before
 * typing anything clinical, then goes into that practice's ordinary form.
 */
export default function PatientRequestStatus() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [wrongPractice, setWrongPractice] = useState(false);

  useEffect(() => {
    let alive = true;
    let timer;
    let tries = 0;
    const load = () => {
      api.get(`/patient-requests/by-token/${token}`)
        .then((r) => {
          if (!alive) return;
          setData(r.data);
          // Poll fast while Stripe confirms the hold, slowly while the office decides.
          tries += 1;
          if (r.data.state === 'requested' && tries < 40) timer = setTimeout(load, 3000);
          else if (r.data.state === 'authorized') timer = setTimeout(load, 60000);
        })
        .catch(() => { if (alive) setError(t('pd.notFound')); });
    };
    load();
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fee = data ? Number(data.fee || 0).toFixed(2) : '';

  let body;
  if (error) {
    body = (
      <div className="flex items-start gap-2 text-sm text-[hsl(0_72%_40%)]" data-testid="pd-status-error">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span>{error}</span>
      </div>
    );
  } else if (!data) {
    body = <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" /> {t('layout.loading')}</div>;
  } else if (data.state === 'requested') {
    body = <div className="flex items-center gap-2 text-sm text-muted-foreground py-4" data-testid="pd-status-checking"><Loader2 className="h-4 w-4 animate-spin" /> {t('pd.checking')}</div>;
  } else if (data.state === 'authorized') {
    body = (
      <div className="space-y-3" data-testid="pd-status-sent">
        <p className="font-medium flex items-start gap-2"><Clock className="h-4 w-4 mt-1 text-primary shrink-0" /> {t('pd.sentLead')}</p>
        <p className="text-sm text-muted-foreground">{t('pd.sentTo', { email: data.office_email })}</p>
        <div className="rounded-lg bg-secondary p-3 text-sm space-y-1">
          <p className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 mt-0.5 text-primary shrink-0" /> {t('pd.heldNotCharged', { fee })}</p>
          {data.days_left != null && <p className="text-xs text-muted-foreground pl-6">{t('pd.daysLeft', { days: data.days_left })}</p>}
        </div>
        <p className="text-xs text-muted-foreground">{t('pd.whatNext')}</p>
      </div>
    );
  } else if (data.state === 'accepted') {
    body = wrongPractice ? (
      <div className="space-y-3" data-testid="pd-status-wrong-practice">
        <p className="text-sm text-muted-foreground">{t('intake.wrongPracticeBody')}</p>
        <div className="rounded-lg bg-secondary p-3 flex items-start gap-2 text-sm">
          <PhoneCall className="h-4 w-4 mt-0.5 text-primary shrink-0" /><span>{t('intake.wrongPracticeHint')}</span>
        </div>
        <Button variant="outline" onClick={() => setWrongPractice(false)}>{t('intake.goBack')}</Button>
      </div>
    ) : (
      <div className="space-y-3" data-testid="pd-status-accepted">
        <p className="font-medium flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-1 text-primary shrink-0" /> {t('pd.acceptedLead')}</p>
        <p className="text-sm font-semibold">{t('intake.confirmTitle')}</p>
        <div className="rounded-xl border bg-card p-4 flex items-center gap-2.5" data-testid="pd-practice-card">
          <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0"><Building2 className="h-4.5 w-4.5" /></span>
          <p className="font-semibold leading-tight">{data.practice_name}</p>
        </div>
        <p className="text-xs text-muted-foreground">{t('intake.whyConfirm')}</p>
        <p className="text-sm text-muted-foreground">{t('pd.acceptedNext')}</p>
        <Button className="w-full h-11" disabled={!data.magic_link} onClick={() => navigate(`${data.magic_link}/form`)} data-testid="pd-continue-form">
          {t('intake.yesButton')}
        </Button>
        <Button variant="outline" className="w-full" onClick={() => setWrongPractice(true)} data-testid="pd-not-my-practice">
          {t('intake.noButton')}
        </Button>
        <p className="text-xs text-muted-foreground">{t('pd.charged', { fee })}</p>
      </div>
    );
  } else {
    body = (
      <div className="space-y-3" data-testid="pd-status-closed">
        <p className="font-medium flex items-start gap-2"><XCircle className="h-4 w-4 mt-1 text-muted-foreground shrink-0" /> {t('pd.closedLead')}</p>
        <p className="text-sm text-muted-foreground">{t('pd.closedBody')}</p>
        <p className="text-xs text-muted-foreground">{t('pd.closedNext')}</p>
        <Button asChild variant="outline"><Link to="/start">{t('pd.startAgain')}</Link></Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-wash grid place-items-center p-4 sm:p-6">
      <Card className="w-full max-w-md" data-testid="pd-status-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-lg">{t('pd.statusTitle')}</CardTitle>
            <LangToggle />
          </div>
        </CardHeader>
        <CardContent>{body}</CardContent>
      </Card>
    </div>
  );
}
